import { useEffect, useRef, useState } from "react";
import { useInView, animate } from "framer-motion";
import { CHAPTER_DEEP } from "./serviceTheme";

// Decorative corner blobs — structure borrowed from a reference "statistics
// card" template (solid-color card + soft SVG shapes bleeding from the top
// right corner). Recolored to a single paper-tinted opacity (no white/fuchsia
// mix) so they read as quiet texture behind the number, not decoration
// competing with it. One blob per chapter color, for a little variety.
const BLOBS = {
  orange: (
    <svg className="absolute right-0 top-0 h-full w-2/3 pointer-events-none" viewBox="0 0 300 200" fill="none" aria-hidden="true">
      <circle cx="220" cy="100" r="90" fill="#F0EAE0" fillOpacity="0.08" />
      <circle cx="260" cy="60" r="60" fill="#F0EAE0" fillOpacity="0.10" />
      <circle cx="270" cy="150" r="30" fill="#F0EAE0" fillOpacity="0.12" />
    </svg>
  ),
  cobalt: (
    <svg className="absolute right-0 top-0 w-2/3 h-full pointer-events-none" viewBox="0 0 200 200" fill="none" aria-hidden="true">
      <ellipse cx="170" cy="60" rx="46" ry="20" fill="#F0EAE0" fillOpacity="0.12" />
      <rect x="120" y="10" width="70" height="24" rx="10" fill="#F0EAE0" fillOpacity="0.09" />
      <circle cx="180" cy="110" r="18" fill="#F0EAE0" fillOpacity="0.15" />
    </svg>
  ),
  lime: (
    <svg className="absolute right-0 top-0 w-2/3 h-full pointer-events-none" viewBox="0 0 200 200" fill="none" aria-hidden="true">
      <polygon points="200,0 200,110 90,0" fill="#F0EAE0" fillOpacity="0.08" />
      <ellipse cx="170" cy="45" rx="34" ry="20" fill="#F0EAE0" fillOpacity="0.13" />
      <circle cx="150" cy="35" r="16" fill="#F0EAE0" fillOpacity="0.17" />
    </svg>
  ),
};

// One editorial "stat cube" — solid-color card, a big count-up number plus
// a caption line. No bounce, no casino sparkle: a single ease-out tween
// that fires once, the moment the cube enters the viewport. Numbers with a
// `staticValue` (e.g. "≈ 2/3") skip the animation entirely and just render
// as text.
export function StatCube({ value, prefix = "", suffix = "", decimals = 0, staticValue, label, testId, colorKey = "orange" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView || staticValue) return;
    const controls = animate(0, value, {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [inView, value, staticValue]);

  const bg = CHAPTER_DEEP[colorKey] ?? CHAPTER_DEEP.orange;
  const blob = BLOBS[colorKey] ?? BLOBS.orange;

  return (
    <div
      ref={ref}
      className="relative overflow-hidden p-7 md:p-8"
      style={{ backgroundColor: bg }}
      data-testid={testId}
    >
      {blob}
      <span className="relative z-10 block font-body font-semibold tracking-tight text-5xl md:text-6xl leading-none text-paper [text-wrap:balance]">
        {staticValue ? staticValue : `${prefix}${display.toFixed(decimals)}${suffix}`}
      </span>
      <p className="relative z-10 mt-4 font-body text-base text-paper/75 leading-snug max-w-[16rem]">
        {label}
      </p>
    </div>
  );
}
